import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableChargerStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportChargerStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchChargerReportStation,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setChargerStationReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportChargerStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { convertMinutes } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * Reports management page
 */
function ChargerReportTableStation(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isChargerStationReportUpdate,
  } = useAppSelector((state) => state.reportChargerStation);
  const dispatch = useAppDispatch();
  const { sortBy, sortField } = useAppSelector((state) => state.reportChargerStation);
  const reportsData = useAppSelector((state) => state.reportData);

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableChargerStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableChargerStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableChargerStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableChargerStationReport,
      sortAllow: true,
    },
    {
      label: t('uptime'),
      accessorKey: 'uptime' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">{convertMinutes(report.uptime)}</div>
      ),
    },
    {
      label: t('downtime'),
      accessorKey: 'downtime' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">{convertMinutes(report.uptime)}</div>
      ),
    },

    {
      label: t('idle'),
      accessorKey: 'idle' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">{convertMinutes(report.uptime)}</div>
      ),
    },
    {
      label: t('inUse'),
      accessorKey: 'inuse' as keyof AvailableChargerStationReport,
      sortAllow: true,
    },
  ];
  /**
   * Fetches Charger report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchChargerReportStation({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
    });
  };

  /**
   * Current columns for Charger Station Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('uptime'), accessorKey: 'uptime' },
    { label: t('downtime'), accessorKey: 'downtime' },
    { label: t('idle'), accessorKey: 'idle' },
    { label: t('inUse'), accessorKey: 'inuse' }, // backend key
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchChargerStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: AvailableChargerStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount ?? '0';

              case 'uptime':
                return row?.uptime ?? '0';

              case 'downtime':
                return row?.downtime ?? '0';

              case 'idle':
                return row?.idle ?? '0';

              case 'inuse':
                return row?.inuse ?? '0';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param value
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    });
  };
  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isChargerStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setChargerStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isChargerStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'chargerUtilizationReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={window.innerHeight >= 700 ? 320 : 339}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          ReferenceCurrentPages={ReferenceCurrentPages}
          sizeChangeButtonRequired
          sortData={{ sortBy, sortField }}
          onHiddenColumnsChange={setHiddenColumns}
          handleSortChange={handleSortFunction}
        />
      </div>
    </div>
  );
}

export default ChargerReportTableStation;
